import { formatDateToLocal } from '@safetyhub/shared';
import {
  getContactSignatureByToken,
  submitContactSignatureByToken,
  type ContactSignatureTokenInfo,
} from '../../lib/contact-signature';
import { WorkplaceSignModal } from '../../components/approval/WorkplaceSignModal';
import {
  ExternalSignatureShell,
  type ExternalApiError,
  type LoaderResult,
} from '../../components/external-signature/ExternalSignatureShell';

type SignResult = { signedAt: string };

async function loadInfo(token: string): Promise<LoaderResult<ContactSignatureTokenInfo>> {
  try {
    const info = await getContactSignatureByToken(token);
    return { ok: true, info };
  } catch (err: any) {
    const apiError: ExternalApiError = err?.response?.data?.error ?? {
      code: 'NETWORK_ERROR',
      message: '서버에 연결할 수 없습니다. 잠시 후 다시 시도해주세요.',
    };
    return { ok: false, error: apiError };
  }
}

const ERROR_TITLES: Record<string, string> = {
  REPORT_CONTACT_SIGNATURE_TOKEN_NOT_FOUND: '링크를 찾을 수 없습니다',
  REPORT_CONTACT_SIGNATURE_TOKEN_EXPIRED: '링크가 만료되었습니다',
  REPORT_CONTACT_SIGNATURE_ALREADY_SIGNED: '이미 서명 완료',
  NETWORK_ERROR: '연결 오류',
};

export default function ContactSignaturePage() {
  return (
    <ExternalSignatureShell<ContactSignatureTokenInfo, SignResult>
      loader={loadInfo}
      extractReport={(info) => info.report}
      toolbar={{
        badgeLabel: () => '담당자 서명',
        badgeTone: 'amber',
        title: (info) => info.report.title,
        subtitle: (info) => {
          const parts: string[] = [info.report.site?.name ?? ''];
          const meta = [info.signerPosition, info.signerDepartment].filter(Boolean).join(' · ');
          if (meta) parts.push(`${info.signerName} · ${meta}`);
          else parts.push(info.signerName);
          parts.push(formatDateToLocal(new Date(info.report.createdAt)));
          return parts.filter(Boolean).join(' | ');
        },
        actionLabel: '서명하기',
        actionButtonClassName: 'bg-orange-600 hover:bg-orange-700',
      }}
      loadingMessage="서명 요청 정보를 불러오는 중..."
      processingMessage="서명을 제출하고 있습니다..."
      errorTitleMap={ERROR_TITLES}
      renderModal={({ info, token, isOpen, onClose, onSubmitted, setProcessing, setError }) => (
        <WorkplaceSignModal
          isOpen={isOpen}
          onClose={onClose}
          defaultSignerName={info.signerName}
          defaultSignerTitle={info.signerPosition ?? undefined}
          defaultSignerDepartment={info.signerDepartment ?? undefined}
          allowSignerEdit
          onSubmit={async (params) => {
            setProcessing();
            try {
              const result = await submitContactSignatureByToken(token, {
                signatureData: params.signatureData,
                signatureMethod: (params.signatureMethod as 'DRAWN' | 'TYPED' | 'DIGITAL') || 'DRAWN',
                ...(params.updateSigner && {
                  updateSigner: true,
                  signerName: params.signerName,
                  signerPosition: params.signerTitle ?? '',
                  signerDepartment: params.signerDepartment ?? '',
                }),
              });
              onSubmitted({ signedAt: result.signedAt });
            } catch (err: any) {
              const apiError: ExternalApiError = err?.response?.data?.error ?? {
                code: 'NETWORK_ERROR',
                message: '서명 제출에 실패했습니다. 다시 시도해주세요.',
              };
              setError(apiError);
            }
          }}
        />
      )}
      renderSuccess={() => (
        <div className="min-h-screen bg-ink-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-card shadow-lg p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
              <span className="h-8 w-8 text-green-600 text-2xl">&#10003;</span>
            </div>
            <h1 className="text-xl font-semibold text-ink-900 mb-2">
              서명이 완료되었습니다
            </h1>
            <p className="text-ink-600 mb-6">협조해 주셔서 감사합니다.</p>
          </div>
        </div>
      )}
      spinnerColorClass="border-orange-600"
    />
  );
}
